import styled from 'styled-components';
import Link from 'next/link';
import DownloadOutlined from '@ant-design/icons/lib/icons/DownloadOutlined';
import { BookIcon } from 'components/Icons';
import { useDispatch } from 'react-redux';
import { getToken, isTokenValid } from 'lib/authentication';
import { logout, openLoginModal } from 'reducer/actions';
import { useCallback } from 'react';

const ButtonGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-bottom: 10px;
`;

const DownloadPDFContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.grey};
    padding: 0.5rem 0.6rem 0.5rem 0;
    min-height: 58px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const DownloadPDFButton = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    > div {
        display: flex;
        text-align: center;

        font-size: 1em;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;

        letter-spacing: normal;
        display: flex;
        color: ${({ theme }) => theme.colors.primary};
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const ShowInteractiveIssueContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.primaryBackground};
    padding: 0.5rem 0.6rem 0.5rem 0;
    min-height: 58px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const InteractiveIssueButton = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    > div {
        display: flex;
        text-align: center;

        font-size: 1em;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;

        letter-spacing: normal;
        display: flex;
        color: ${({ theme }) => theme.colors.white};
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const DownloadPDF = ({
    url,
    isLoginRequired
}: {
    url: string;
    isLoginRequired: boolean;
}): React.JSX.Element => {
    const dispatch = useDispatch();
    const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
        if (isLoginRequired && !isTokenValid()) {
            event.preventDefault();
            dispatch(logout());
            dispatch(openLoginModal());
            return;
        }
    };
    return (
        <DownloadPDFContainer>
            <Link href={url}>
                <DownloadPDFButton onClick={handleDownloadClick}>
                    <div>
                        <div style={{ flex: '1 1 auto', paddingRight: '5px' }}>
                            <DownloadOutlined style={{ fontSize: 24 }} />
                        </div>
                        <div style={{ flex: '1 1 auto' }}>
                            Pobierz
                            <br /> PDF
                        </div>
                    </div>
                </DownloadPDFButton>
            </Link>
        </DownloadPDFContainer>
    );
};

const InteractiveIssue = ({
    url,
    isLoginRequired
}: {
    url: string;
    isLoginRequired: boolean;
}): React.JSX.Element => {
    const dispatch = useDispatch();
    const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
        if (isLoginRequired && !isTokenValid()) {
            event.preventDefault();
            dispatch(logout());
            dispatch(openLoginModal());
            return;
        }
    };
    const isAvailable = useCallback(() => {
        return isLoginRequired ? isTokenValid() : true;
    }, [isLoginRequired]);

    return (
        <ShowInteractiveIssueContainer>
            <a
                href={isAvailable() && url}
                target="_blank"
                rel="nofollow noreferrer"
                onClick={handleDownloadClick}
            >
                <InteractiveIssueButton>
                    <div>
                        <div style={{ flex: '1 1 auto', paddingRight: 10 }}>
                            <BookIcon style={{ fontSize: 30 }} />
                        </div>
                        <div style={{ flex: '1 1 auto' }}>
                            Przeczytaj
                            <br /> e-wydanie
                        </div>
                    </div>
                </InteractiveIssueButton>
            </a>
        </ShowInteractiveIssueContainer>
    );
};

type Props = {
    $issue: Issue;
};

export const ActionButtons = ({ $issue }: Props) => {
    let downloadLink = $issue?.whitePaper?.downloadLink;

    if ($issue?.whitePaper?.isSecure) {
        downloadLink = `${$issue?.whitePaper?.downloadLink}?bearer=${getToken()}`;
    }

    if (!downloadLink && $issue.eIssueUrl) {
        return (
            <InteractiveIssue url={$issue.eIssueUrl} isLoginRequired={$issue.eIssueUrlProtected} />
        );
    }

    if (downloadLink && !$issue.eIssueUrl) {
        return <DownloadPDF url={downloadLink} isLoginRequired={$issue?.whitePaper?.isSecure} />;
    }

    return (
        <ButtonGrid>
            {downloadLink && (
                <DownloadPDF url={downloadLink} isLoginRequired={$issue?.whitePaper?.isSecure} />
            )}
            {$issue.eIssueUrl && (
                <InteractiveIssue
                    url={$issue.eIssueUrl}
                    isLoginRequired={$issue.eIssueUrlProtected}
                />
            )}
        </ButtonGrid>
    );
};

export default ActionButtons;
